import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CalendarDays, Clock3, MapPin, ShieldCheck } from "lucide-react";
import { api } from "../../../services/apiService";
import { CheckoutHeader } from "../components/CheckoutHeader";
import { DateTimeField } from "../../../components/ops/DateTimeField";
import { SignatureCanvas } from "../../../components/schedule/SignatureCanvas";
import { ageGroupLabels, day, time } from "../../../components/schedule/scheduleUtils";
import { formatMoney, paymentError } from "../../../components/payments/paymentUtils";

const waiverText = `Formula Soccer Center — Liability Waiver and Release

By checking the box below, you acknowledge that youth sports involve inherent risks of injury, and you voluntarily assume those risks on behalf of the enrolled player. You release Formula Soccer Center, its coaches, and staff from liability for injuries sustained during training sessions, except in cases of gross negligence.

You confirm that the player is physically able to participate in training activities, and you agree to notify staff of any relevant medical conditions.

This waiver applies to this single booked session.`;

const emptyPlayer = { firstName: "", lastName: "", dateOfBirth: null };
const emptyGuardian = { firstName: "", lastName: "", email: "", phone: "" };
const emptyWaiver = { relationshipToPlayer: "", emergencyContactName: "", emergencyContactPhone: "", secondaryEmergencyContactName: "", secondaryEmergencyContactPhone: "", physicianName: "", notes: "", acknowledgedTerms: false, signatureName: "", signatureImage: "" };

export function GuestSessionBookingPage() {
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const priorReference = params.get("reference");

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [player, setPlayer] = useState(emptyPlayer);
  const [guardian, setGuardian] = useState(emptyGuardian);
  const [waiver, setWaiver] = useState(emptyWaiver);
  const [submitting, setSubmitting] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/schedule/public/sessions/${sessionId}`)
      .then((response) => setSession(response.data.session))
      .catch((err) => setLoadError(paymentError(err, "This session could not be found.")))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const remaining = session ? Math.max(0, session.capacity - (session.occupancy?.booked || 0)) : 0;

  const resumePrevious = async () => {
    setResuming(true); setError("");
    try {
      const response = await api.post(`/checkout/guest-session/${encodeURIComponent(priorReference)}/resume`);
      window.location.href = response.data.alreadyPaid ? `/book-session/confirmed?reference=${priorReference}` : response.data.checkoutUrl;
    } catch (err) {
      setError(paymentError(err, "Unable to resume that payment. You can fill out the form below to try again."));
      setResuming(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!player.firstName.trim() || !player.lastName.trim() || !player.dateOfBirth) return setError("Enter the player's first name, last name, and date of birth.");
    if (!guardian.firstName.trim() || !guardian.lastName.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guardian.email.trim()) || !guardian.phone.trim()) return setError("Enter a guardian name, a valid email, and a phone number.");
    if (!waiver.emergencyContactName.trim() || !waiver.emergencyContactPhone.trim()) return setError("An emergency contact name and phone number are required.");
    if (!waiver.acknowledgedTerms) return setError("Please acknowledge the waiver terms to continue.");
    if (!waiver.signatureName.trim()) return setError("Type your full name as your signature.");
    if (!waiver.signatureImage) return setError("Draw your signature above to continue.");
    setSubmitting(true);
    try {
      const response = await api.post("/checkout/guest-session", { sessionId, player: { ...player, dateOfBirth: player.dateOfBirth.toISOString() }, guardian, waiver });
      window.location.href = response.data.checkoutUrl;
    } catch (err) {
      setError(paymentError(err, "Unable to start checkout. Please try again."));
      setSubmitting(false);
    }
  };

  if (loading) return <main className="checkout-wrap"><CheckoutHeader />Loading…</main>;
  if (loadError || !session) {
    return (
      <main className="checkout-wrap">
        <CheckoutHeader />
        <div className="ops-alert ops-alert--error">{loadError || "This session could not be found."}</div>
        <Link to="/schedule" className="ops-card-link">Back to schedule</Link>
      </main>
    );
  }

  return (
    <main className="checkout-wrap">
      <CheckoutHeader />

      <div className="ops-card" style={{ marginBottom: 18 }}>
        <p className="ops-eyebrow">Book as a guest — no account required</p>
        <h1 className="ops-header-title" style={{ marginBottom: 6 }}>{session.title}</h1>
        <div className="schedule-session-meta" style={{ marginTop: 10 }}>
          <span><CalendarDays size={14} />{day(session.startsAt)}</span>
          <span><Clock3 size={14} />{time(session.startsAt)} – {time(session.endsAt)}</span>
          <span><MapPin size={14} />{session.location}</span>
        </div>
        <div className="ops-stat-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)", marginTop: 16 }}>
          <div className="ops-stat-card"><div className="ops-stat-card-content"><p className="ops-stat-card-label">Price</p><p className="ops-stat-value">{formatMoney(session.dropInPriceCents)}</p><p className="ops-stat-hint">one-time, this session only</p></div></div>
          <div className="ops-stat-card"><div className="ops-stat-card-content"><p className="ops-stat-card-label">Availability</p><p className="ops-stat-value">{remaining}</p><p className="ops-stat-hint">of {session.capacity} spots open · {ageGroupLabels[session.ageGroup]}</p></div></div>
        </div>
      </div>

      {priorReference && (
        <div className="ops-card" style={{ marginBottom: 18 }}>
          <p style={{ margin: 0, fontSize: 14 }}>Your previous attempt wasn't completed.</p>
          <div className="ops-form-actions"><button type="button" className="ops-button ops-button--secondary" onClick={resumePrevious} disabled={resuming}>{resuming ? "Opening secure checkout…" : "Resume that payment"}</button></div>
        </div>
      )}

      {!remaining ? (
        <div className="ops-card">
          <h2 className="ops-card-title">This session is full</h2>
          <p style={{ color: "var(--ops-muted)", fontSize: 14 }}>Sign in to a parent account to join the waitlist, or choose a different session.</p>
          <div className="ops-form-actions"><Link to="/schedule" className="ops-button ops-button--secondary">Back to schedule</Link><Link to="/login" className="ops-button ops-button--primary">Sign in</Link></div>
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="ops-card" style={{ marginBottom: 18 }}>
            <h2 className="ops-card-title">Player details</h2>
            <div className="ops-form-grid">
              <div><label className="ops-form-label">First name</label><input required maxLength={80} className="ops-input" value={player.firstName} onChange={(e) => setPlayer({ ...player, firstName: e.target.value })} /></div>
              <div><label className="ops-form-label">Last name</label><input required maxLength={80} className="ops-input" value={player.lastName} onChange={(e) => setPlayer({ ...player, lastName: e.target.value })} /></div>
              <div><label className="ops-form-label" htmlFor="guest-player-dob">Date of birth</label><DateTimeField id="guest-player-dob" required showTime={false} maxDate={new Date()} value={player.dateOfBirth} onChange={(date) => setPlayer({ ...player, dateOfBirth: date })} placeholder="Select date of birth" /></div>
            </div>
          </div>

          <div className="ops-card" style={{ marginBottom: 18 }}>
            <h2 className="ops-card-title">Guardian contact</h2>
            <p style={{ color: "var(--ops-muted)", fontSize: 13, marginTop: -6, marginBottom: 14 }}>Used for your receipt and parent portal access after checkout.</p>
            <div className="ops-form-grid">
              <div><label className="ops-form-label">First name</label><input required maxLength={80} className="ops-input" value={guardian.firstName} onChange={(e) => setGuardian({ ...guardian, firstName: e.target.value })} /></div>
              <div><label className="ops-form-label">Last name</label><input required maxLength={80} className="ops-input" value={guardian.lastName} onChange={(e) => setGuardian({ ...guardian, lastName: e.target.value })} /></div>
              <div><label className="ops-form-label">Email</label><input required type="email" className="ops-input" value={guardian.email} onChange={(e) => setGuardian({ ...guardian, email: e.target.value })} /></div>
              <div><label className="ops-form-label">Phone</label><input required type="tel" maxLength={40} className="ops-input" value={guardian.phone} onChange={(e) => setGuardian({ ...guardian, phone: e.target.value })} /></div>
            </div>
          </div>

          <div className="ops-card">
            <h2 className="ops-card-title">Waiver &amp; signature</h2>
            <p className="checkout-payment-note">You'll be sent to Stripe for a secure, one-time payment right after signing. This session is booked only once payment is confirmed.</p>
            <div className="ops-waiver-box">{waiverText}</div>

            <div className="ops-form-grid" style={{ marginTop: 16 }}>
              <div><label className="ops-form-label">Relationship to player <span className="ops-form-label-optional">(optional)</span></label><input maxLength={80} className="ops-input" placeholder="Parent, guardian…" value={waiver.relationshipToPlayer} onChange={(e) => setWaiver({ ...waiver, relationshipToPlayer: e.target.value })} /></div>
              <div><label className="ops-form-label">Emergency contact name</label><input required maxLength={160} className="ops-input" value={waiver.emergencyContactName} onChange={(e) => setWaiver({ ...waiver, emergencyContactName: e.target.value })} /></div>
              <div><label className="ops-form-label">Emergency contact phone</label><input required type="tel" maxLength={40} className="ops-input" value={waiver.emergencyContactPhone} onChange={(e) => setWaiver({ ...waiver, emergencyContactPhone: e.target.value })} /></div>
              <div><label className="ops-form-label">Secondary emergency contact <span className="ops-form-label-optional">(optional)</span></label><input maxLength={160} className="ops-input" value={waiver.secondaryEmergencyContactName} onChange={(e) => setWaiver({ ...waiver, secondaryEmergencyContactName: e.target.value })} /></div>
              <div><label className="ops-form-label">Secondary contact phone <span className="ops-form-label-optional">(optional)</span></label><input type="tel" maxLength={40} className="ops-input" value={waiver.secondaryEmergencyContactPhone} onChange={(e) => setWaiver({ ...waiver, secondaryEmergencyContactPhone: e.target.value })} /></div>
              <div><label className="ops-form-label">Primary care physician <span className="ops-form-label-optional">(optional)</span></label><input maxLength={160} className="ops-input" value={waiver.physicianName} onChange={(e) => setWaiver({ ...waiver, physicianName: e.target.value })} /></div>
            </div>
            <div className="ops-form-group" style={{ marginTop: 4 }}>
              <label className="ops-form-label">Notes for staff <span className="ops-form-label-optional">(optional)</span></label>
              <textarea maxLength={500} className="ops-textarea" placeholder="Allergies, medical conditions, or anything staff should know…" value={waiver.notes} onChange={(e) => setWaiver({ ...waiver, notes: e.target.value })} />
            </div>

            <label className="ops-checkbox-row" style={{ marginTop: 16 }}>
              <input type="checkbox" checked={waiver.acknowledgedTerms} onChange={(e) => setWaiver({ ...waiver, acknowledgedTerms: e.target.checked })} />
              I have read and accept the terms of this waiver on behalf of the player named above.
            </label>

            <div className="ops-form-group" style={{ marginTop: 16 }}>
              <label className="ops-form-label">Signature</label>
              <SignatureCanvas onChange={(image) => setWaiver({ ...waiver, signatureImage: image })} disabled={submitting} />
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Type your full name to confirm</label>
              <input required maxLength={160} className="ops-input" placeholder="Full legal name" value={waiver.signatureName} onChange={(e) => setWaiver({ ...waiver, signatureName: e.target.value })} />
            </div>

            {error && <div className="ops-alert ops-alert--error" style={{ marginTop: 16 }}>{error}</div>}

            <div className="ops-form-actions">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--ops-muted)", fontSize: 12 }}><ShieldCheck size={14} /> Secured by Stripe</span>
              <button type="submit" className="ops-button ops-button--primary" disabled={submitting}>{submitting ? "Redirecting to payment…" : `Sign waiver & pay ${formatMoney(session.dropInPriceCents)}`}</button>
            </div>
          </div>
        </form>
      )}
    </main>
  );
}
